import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3,
  Grid,
  Calendar,
  Filter,
  Download,
  Share,
  Eye,
  EyeOff,
  Layers,
  Target,
  Clock,
  Users,
  TrendingUp,
  Zap
} from 'lucide-react';
import { demoFeatures, demoRoadmap } from '../../data/demoData';
import { Feature } from '../../types';

const RoadmapVisualization = () => {
  const [viewMode, setViewMode] = useState<'document' | 'flow'>('document');
  const [selectedQuarter, setSelectedQuarter] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [showDependencies, setShowDependencies] = useState(true);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);

  const quarters = ['Q1 2024', 'Q2 2024', 'Q3 2024', 'Q4 2024'];
  const priorities = ['critical', 'high', 'medium', 'low'];

  const filteredFeatures = demoFeatures.filter(feature => {
    const quarterMatch = selectedQuarter === 'all' || feature.quarter === selectedQuarter;
    const priorityMatch = selectedPriority === 'all' || feature.priority === selectedPriority;
    return quarterMatch && priorityMatch;
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'from-red-500 to-red-600';
      case 'high': return 'from-orange-500 to-orange-600';
      case 'medium': return 'from-yellow-500 to-yellow-600';
      case 'low': return 'from-green-500 to-green-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const getQuarterColor = (quarter: string) => {
    switch (quarter) {
      case 'Q1 2024': return 'from-blue-500 to-blue-600';
      case 'Q2 2024': return 'from-purple-500 to-purple-600';
      case 'Q3 2024': return 'from-green-500 to-green-600';
      case 'Q4 2024': return 'from-orange-500 to-orange-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  return (
    <div className="h-full bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto h-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold gradient-text mb-2">Roadmap Visualization</h1>
              <p className="text-slate-600 dark:text-slate-400">
                Interactive views and comprehensive analysis of your product roadmap
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-lg border border-slate-200/50 dark:border-slate-700/50">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setViewMode('document')}
                  className={`px-4 py-2 rounded-l-lg flex items-center space-x-2 transition-all duration-200 ${
                    viewMode === 'document' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  <span>Document</span>
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setViewMode('flow')}
                  className={`px-4 py-2 rounded-r-lg flex items-center space-x-2 transition-all duration-200 ${
                    viewMode === 'flow' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                  <span>Flow</span>
                </motion.button>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary flex items-center space-x-2"
              >
                <Share className="w-4 h-4" />
                <span>Share</span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </motion.button>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              <select
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
                className="px-3 py-2 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-lg text-sm"
              >
                <option value="all">All Quarters</option>
                {quarters.map(quarter => (
                  <option key={quarter} value={quarter}>{quarter}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Target className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                className="px-3 py-2 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-lg text-sm"
              >
                <option value="all">All Priorities</option>
                {priorities.map(priority => (
                  <option key={priority} value={priority}>
                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowDependencies(!showDependencies)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                showDependencies 
                  ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
              }`}
            >
              {showDependencies ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span className="text-sm">Dependencies</span>
            </motion.button>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 h-[calc(100vh-250px)]">
          <div className="xl:col-span-3">
            <AnimatePresence mode="wait">
              {viewMode === 'document' ? (
                <motion.div
                  key="document-view"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 h-full overflow-auto"
                >
                  <div className="space-y-8">
                    {quarters.map((quarter) => {
                      const quarterFeatures = filteredFeatures.filter(f => f.quarter === quarter);
                      if (quarterFeatures.length === 0 && selectedQuarter !== 'all') return null;
                      
                      return (
                        <motion.div
                          key={quarter}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-4"
                        >
                          <div className="flex items-center space-x-3 mb-6">
                            <div className={`w-12 h-12 bg-gradient-to-r ${getQuarterColor(quarter)} rounded-xl flex items-center justify-center`}>
                              <Calendar className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                                {quarter}
                              </h3>
                              <p className="text-sm text-slate-600 dark:text-slate-400">
                                {quarterFeatures.length} features planned
                              </p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {quarterFeatures.map((feature, index) => (
                              <motion.div
                                key={feature.id}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                                whileHover={{ scale: 1.02 }}
                                onClick={() => setSelectedFeature(feature)}
                                className="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer card-hover"
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
                                      {feature.name}
                                    </h4>
                                    <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                                      {feature.description}
                                    </p>
                                  </div>
                                  
                                  <div className={`px-2 py-1 rounded-full text-xs font-medium ml-3 ${
                                    feature.priority === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                                    feature.priority === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                                    feature.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                                    'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                  }`}>
                                    {feature.priority}
                                  </div>
                                </div>
                                
                                <div className="flex items-center justify-between text-xs">
                                  <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-1">
                                      <TrendingUp className="w-3 h-3 text-green-500" />
                                      <span className="text-slate-600 dark:text-slate-400">
                                        Impact: {feature.impact}/10
                                      </span>
                                    </div>
                                    
                                    <div className="flex items-center space-x-1">
                                      <Clock className="w-3 h-3 text-orange-500" />
                                      <span className="text-slate-600 dark:text-slate-400">
                                        {feature.estimatedHours}h
                                      </span>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center space-x-1">
                                    <Users className="w-3 h-3 text-blue-500" />
                                    <span className="text-slate-600 dark:text-slate-400">
                                      {feature.assignedTeam}
                                    </span>
                                  </div>
                                </div>
                                
                                {showDependencies && feature.dependencies.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                                    <div className="text-xs text-slate-500 dark:text-slate-500 mb-1">
                                      Dependencies:
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                      {feature.dependencies.map(dep => (
                                        <span
                                          key={dep}
                                          className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-xs text-slate-600 dark:text-slate-400"
                                        >
                                          {dep}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="flow-view"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 h-full overflow-hidden"
                >
                  <div className="relative w-full h-full">
                    <svg
                      width="100%"
                      height="100%"
                      viewBox="0 0 1200 800"
                      className="absolute inset-0"
                    >
                      <defs>
                        <pattern id="flowGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" className="text-slate-200 dark:text-slate-700" opacity="0.3"/>
                        </pattern>
                      </defs>
                      
                      <rect width="100%" height="100%" fill="url(#flowGrid)" />
                      
                      {filteredFeatures.map((feature, index) => {
                        const x = 150 + (index % 4) * 250;
                        const y = 100 + Math.floor(index / 4) * 150;
                        
                        return (
                          <motion.g
                            key={feature.id}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.1 }}
                          >
                            <motion.rect
                              x={x - 75}
                              y={y - 40}
                              width="150"
                              height="80"
                              rx="12"
                              className={`cursor-pointer transition-all duration-300 fill-white dark:fill-slate-800 stroke-2 ${
                                feature.priority === 'critical' ? 'stroke-red-500' :
                                feature.priority === 'high' ? 'stroke-orange-500' :
                                feature.priority === 'medium' ? 'stroke-yellow-500' :
                                'stroke-green-500'
                              }`}
                              onClick={() => setSelectedFeature(feature)}
                              whileHover={{ scale: 1.05 }}
                            />
                            
                            <text
                              x={x}
                              y={y - 15}
                              textAnchor="middle"
                              className="fill-current text-xs font-medium text-slate-900 dark:text-slate-100 pointer-events-none"
                            >
                              {feature.name.split(' ').slice(0, 2).join(' ')}
                            </text>
                            
                            <text
                              x={x}
                              y={y}
                              textAnchor="middle"
                              className="fill-current text-xs text-slate-600 dark:text-slate-400 pointer-events-none"
                            >
                              {feature.quarter}
                            </text>
                            
                            <text
                              x={x}
                              y={y + 15}
                              textAnchor="middle"
                              className="fill-current text-xs font-medium text-slate-700 dark:text-slate-300 pointer-events-none"
                            >
                              Impact: {feature.impact}/10
                            </text>
                            
                            <circle
                              cx={x + 60}
                              cy={y - 25}
                              r="8"
                              className={`${
                                feature.priority === 'critical' ? 'fill-red-500' :
                                feature.priority === 'high' ? 'fill-orange-500' :
                                feature.priority === 'medium' ? 'fill-yellow-500' :
                                'fill-green-500'
                              }`}
                            />
                          </motion.g>
                        );
                      })}
                    </svg>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <AnimatePresence mode="wait">
            {selectedFeature ? (
              <motion.div
                key="feature-details"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 h-full overflow-auto"
              >
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2">
                      {selectedFeature.name}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {selectedFeature.description}
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
                        Priority
                      </div>
                      <div className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                        selectedFeature.priority === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                        selectedFeature.priority === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                        selectedFeature.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                        'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      }`}>
                        {selectedFeature.priority}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
                        Impact vs Effort
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-600 dark:text-slate-400">Impact</span>
                          <span className="text-xs font-medium text-slate-900 dark:text-slate-100">
                            {selectedFeature.impact}/10
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full"
                            style={{ width: `${selectedFeature.impact * 10}%` }}
                          ></div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-600 dark:text-slate-400">Effort</span>
                          <span className="text-xs font-medium text-slate-900 dark:text-slate-100">
                            {selectedFeature.effort}/10
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full"
                            style={{ width: `${selectedFeature.effort * 10}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
                        Details
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600 dark:text-slate-400">Quarter:</span>
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {selectedFeature.quarter}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600 dark:text-slate-400">Category:</span>
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {selectedFeature.category}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600 dark:text-slate-400">Team:</span>
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {selectedFeature.assignedTeam}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600 dark:text-slate-400">Hours:</span>
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {selectedFeature.estimatedHours}h
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {selectedFeature.dependencies.length > 0 && (
                      <div>
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
                          Dependencies
                        </div>
                        <div className="space-y-1">
                          {selectedFeature.dependencies.map(dep => (
                            <div
                              key={dep}
                              className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-xs text-slate-600 dark:text-slate-400"
                            >
                              {dep}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="roadmap-overview"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 dark:border-slate-700/50 p-6 h-full"
              >
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                  Roadmap Overview
                </h3>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {filteredFeatures.length}
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-400">
                        Features
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {Math.round(filteredFeatures.reduce((acc, f) => acc + f.impact, 0) / filteredFeatures.length * 10) / 10}
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-400">
                        Avg Impact
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-3">
                      Priority Breakdown
                    </div>
                    <div className="space-y-2">
                      {priorities.map(priority => {
                        const count = filteredFeatures.filter(f => f.priority === priority).length;
                        const percentage = filteredFeatures.length > 0 ? (count / filteredFeatures.length) * 100 : 0;
                        
                        return (
                          <div key={priority} className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${getPriorityColor(priority)}`}></div>
                              <span className="text-xs text-slate-600 dark:text-slate-400 capitalize">
                                {priority}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="w-12 bg-slate-200 dark:bg-slate-700 rounded-full h-1.5">
                                <div
                                  className={`bg-gradient-to-r ${getPriorityColor(priority)} h-1.5 rounded-full`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium text-slate-900 dark:text-slate-100 w-6">
                                {count}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="text-center text-sm text-slate-600 dark:text-slate-400">
                    Click on any feature to view detailed information
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default RoadmapVisualization;